/**
 * Quota-safe localStorage helpers.
 *
 * Browser storage is best-effort: a failed write must never crash the UI or
 * block a business operation (CMS save, product save, media selection).
 * `safeSetItem` swallows quota errors, prunes reclaimable keys, and retries
 * once before giving up silently.
 */

/** Keys that may be pruned automatically when the quota is exhausted. */
const RECLAIMABLE_KEYS = [
  "cb16.audit.log.v1",
  "cb16.tracking.views.v1",
  "cb16.tracking.searches.v1",
  "cb16.pdp.recently-viewed.v1",
];

export function isQuotaError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const name = (err as DOMException).name;
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    /quota/i.test(err.message)
  );
}

/** Drop reclaimable caches to free space. */
function reclaim(exceptKey?: string) {
  for (const key of RECLAIMABLE_KEYS) {
    if (key === exceptKey) continue;
    try { window.localStorage.removeItem(key); } catch { /* ignore */ }
  }
}

/**
 * Write to localStorage without ever throwing.
 * @returns true when the value was persisted.
 */
export function safeSetItem(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (err) {
    if (!isQuotaError(err)) {
      console.warn(`[storage] write failed for ${key}:`, err);
      return false;
    }
    reclaim(key);
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch {
      // Last resort: drop this key entirely rather than block the caller.
      try { window.localStorage.removeItem(key); } catch { /* ignore */ }
      console.warn(`[storage] quota exceeded; skipped write for ${key}`);
      return false;
    }
  }
}

export function safeGetItem(key: string): string | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(key); } catch { return null; }
}

export function safeRemoveItem(key: string) {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(key); } catch { /* ignore */ }
}
